// One frame for a client's record. Every tab (Overview, Care plan, Documents, Billing…) sits under
// the same header, which loads the client once and stays on screen while the tabs change — no more
// full-page spinner and header flash on every tab click.
//
// Route shape (App.jsx):  <Route path="/clients/:id" element={<ClientLayout />}> …tab routes… </Route>
//
// Tab pages call `useClientRecord(id)` and wrap their content in <ClientFrame record={record}>.
// Inside ClientLayout they get the shared record and no second header; rendered on their own (a
// flat route), they load the client themselves and draw the header, so both route shapes work.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useParams } from 'react-router-dom';
import api from '../../utils/api';
import { useClientCtx } from '../../context/ClientContext';
import { PAGE_WIDE, ErrorState } from '../../components/ui';
import ClientHeader from './ClientHeader';
import { fullName } from './clientShared';

const RecordCtx = createContext(null);
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const loadError = (e) => {
  const status = e?.response?.status;
  if (status === 404) return 'This client was not found. It may have been deleted.';
  if (status === 403) return 'You don’t have access to this client.';
  return e?.response?.data?.message || 'We couldn’t load this client. Check your connection, then try again.';
};

// Loads one client (and, when asked, its risk flags). Passing no id loads nothing.
function useLoadClient(id, { withRisk = false, clearOnUnmount = false } = {}) {
  const { setActiveClient } = useClientCtx() || {};
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(!!id);
  const [error, setError] = useState(null);
  const [riskScore, setRiskScore] = useState(null);
  const seq = useRef(0);

  // `quiet` keeps the record on screen while it refreshes after an edit.
  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!id) return null;
    if (!UUID_RE.test(id)) { setLoading(false); setError('This client link isn’t valid.'); return null; }
    const n = ++seq.current;
    if (!quiet) { setLoading(true); setError(null); }
    try {
      const { data } = await api.get(`/clients/${id}`);
      if (n !== seq.current) return null;
      setClient(data);
      setError(null);
      // is_active feeds the sidebar's client chip (Active / Archived).
      if (setActiveClient) setActiveClient({ id: data.id, name: fullName(data), is_active: data.is_active });
      return data;
    } catch (e) {
      if (n !== seq.current) return null;
      if (!quiet) { setClient(null); setError(loadError(e)); }
      return null;
    } finally {
      if (n === seq.current) setLoading(false);
    }
  }, [id, setActiveClient]);

  useEffect(() => {
    if (!id) return undefined;
    setClient(null);
    load();
    return undefined;
  }, [id, load]);

  useEffect(() => {
    if (!id || !withRisk) return undefined;
    let live = true;
    api.get('/clients/risk-scores')
      .then((r) => { if (live) setRiskScore((r.data && r.data[id]) || null); })
      .catch(() => { /* the badge is optional */ });
    return () => { live = false; };
  }, [id, withRisk]);

  useEffect(() => {
    if (!clearOnUnmount || !setActiveClient) return undefined;
    return () => setActiveClient(null);
  }, [clearOnUnmount, setActiveClient]);

  const reload = useCallback(() => load({ quiet: true }), [load]);
  const retry = useCallback(() => load(), [load]);

  return useMemo(() => ({
    id, client, loading, error, riskScore, reload, retry, setClient,
  }), [id, client, loading, error, riskScore, reload, retry]);
}

// The record for the current client: the shared one inside ClientLayout, otherwise loaded here.
export function useClientRecord(id, opts) {
  const shared = useContext(RecordCtx);
  const own = useLoadClient(shared ? null : id, opts);
  return shared || own;
}

// Wraps a tab's content. Inside ClientLayout the header is already on screen; on its own the page
// draws it (and handles a failed load) itself.
export function ClientFrame({ record, children }) {
  if (record && record.inLayout) return <div className="space-y-6">{children}</div>;
  return (
    <div className={PAGE_WIDE}>
      {record && record.error && !record.client ? (
        <div className="bg-white border border-slate-200 rounded-xl">
          <ErrorState message={record.error} onRetry={record.retry} />
        </div>
      ) : (
        <>
          <ClientHeader client={record && record.client} riskScore={record && record.riskScore}
            loading={record ? record.loading : true} onClientUpdated={record && record.reload} />
          <div className="space-y-6">{children}</div>
        </>
      )}
    </div>
  );
}

export default function ClientLayout() {
  const { id } = useParams();
  const record = useLoadClient(id, { withRisk: true, clearOnUnmount: true });
  const value = useMemo(() => ({ ...record, inLayout: true }), [record]);

  return (
    <RecordCtx.Provider value={value}>
      <div className={PAGE_WIDE}>
        {record.error && !record.client ? (
          <div className="bg-white border border-slate-200 rounded-xl">
            <ErrorState message={record.error} onRetry={record.retry} />
          </div>
        ) : (
          <>
            <ClientHeader client={record.client} riskScore={record.riskScore} loading={record.loading}
              onClientUpdated={record.reload} />
            <Outlet />
          </>
        )}
      </div>
    </RecordCtx.Provider>
  );
}
